import { useEffect, useRef } from "react";

/**
 * Fires `onEnter` once when the returned ref's element first scrolls into the viewport, then
 * disconnects — a one-shot lazy-load trigger (`AnchorStage.tsx` uses it to defer loading the
 * stage's video until it's actually visible), not a general scroll-tracking hook. Kept local to
 * this feature rather than added to `@cinesys/ui`'s shared hooks since nothing else needs it yet.
 */
export function useInView<T extends Element>(
  onEnter: () => void,
  rootMargin?: string
) {
  const ref = useRef<T | null>(null);
  // Ref, not a dep — so a re-render that hands in a new inline callback (the common case) doesn't
  // tear down and recreate the observer.
  const onEnterRef = useRef(onEnter);
  onEnterRef.current = onEnter;

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onEnterRef.current();
          observer.disconnect();
        }
      },
      { rootMargin }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin]);

  return ref;
}
